import React from 'react';
import {Fragment, type JSX} from 'react';

import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {Upload} from '../../../icons/Upload';
import {Label, P} from '../../../layout/Text/Text';
import {InfoPopup} from '../../InfoPopup/InfoPopup';
import {FileItem, type FileItemProps} from '../FileItem/FileItem';
import {Hints} from '../Hints/Hints';

import {type ProgressState, useFileInput} from './useFileInput';
import {
    fileSizeStringFromBytes,
    getFileInputDisabledVariables,
    getFileInputErrorVariables,
    getFileInputFocusVariables,
    getFileInputVariables,
    getUploadIconSize
} from './utils';

import type {ValidationFunc} from '@nfq/react-form';

/**
 * Defines the properties for the `FileInput` component, which allows users to upload files
 * with specified constraints like allowed file types and initial values.
 */
export interface ComponentProps {
    /**
     * A override for the default `FileItem` component used to display each uploaded file. This allows for custom rendering of file items in the upload list.
     */
    CustomFileItem?(props: FileItemProps): JSX.Element;
    /**
     * The default value that should be pre-selected when the dropdown is first rendered.
     * This value should correspond to one of the option values in the options array to ensure
     * proper selection state initialization.
     */
    defaultProgresses?: {
        fileErrors: (string | null | undefined)[];
        fileProgressBars: (number | 'done' | undefined)[];
    };
    /**
     * The default value that should be pre-selected when the dropdown is first rendered.
     * This value should correspond to one of the option values in the options array to ensure
     * proper selection state initialization.
     */
    defaultValue?: FileList;
    /**
     * An array of hint messages to display below the file input.
     * These hints provide additional context or guidance to the user about the expected input
     * or usage of the file input.
     *
     * @default undefined
     */
    hints?: string[];
    /**
     * Optional informational text that provides additional context about the file input.
     */
    infoText?: string;
    /**
     * Determines whether the file input should be rendered in a disabled state.
     * When disabled, the dropdown becomes non-interactive and displays visual indicators
     * to show that it cannot be used for selection.
     *
     * @default false
     */
    isDisabled?: boolean;
    /**
     * Indicates whether to retain the field value when the component unmounts.
     *
     * @default false
     */
    isKeepOnUnmount?: boolean;
    /**
     * A boolean flag indicating whether only a single file can be uploaded.
     * This flag restricts the user from uploading multiple files and only allows uploading a single file.
     *
     * @default false
     */
    isSingleFile?: boolean;
    /**
     * The label for the file input.
     */
    label: string;
    /**
     * The max file size in bytes.
     */
    maxFileSize?: number;
    /**
     * The name identifier for the file input field used for form handling and validation.
     * This property is essential for form submission and helps identify the field when processing
     * form data or tracking field-specific validation errors.
     */
    name: string;
    /**
     * Callback function that gets executed when a user selects an option from the dropdown.
     * The function receives the selected value and optionally the field name, allowing parent
     * components to handle state updates and form validation appropriately.
     */
    onChange?(value: FileList, progresses: ProgressState): Promise<void>;
    /**
     * Callback function triggered when the download action is performed on a file item.
     */
    onDownload?(index: number): void;
    /**
     * The size of the file input component, which can be either 'large' or 'small'.
     * This prop determines the overall dimensions and styling of the file input,
     * allowing for different visual presentations based on the specified size.
     */
    size?: 'large' | 'small';
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing scenarios
     * and enables reliable automated testing by providing a consistent selector target.
     *
     * @default 'DropdownInputInput'
     */
    testId?: string;
    /**
     * Optional validation function to validate the input value.
     */
    validation?: ValidationFunc;
}

/**
 * A component that provides a user interface for uploading files with support for drag-and-drop functionality.
 *
 * @param props                 The component props.
 * @param props.defaultValue    The default value that should be pre-selected when the dropdown is first rendered.
 * @param props.hints           An array of hint messages to display below the file input.
 * @param props.infoText        Optional informational text that provides additional context about the file input.
 * @param props.label           The label for the select dropdown.
 * @param props.isDisabled      Determines whether the file input should be rendered in a disabled state.
 * @param props.isKeepOnUnmount Indicates whether to retain the field value when the component unmounts.
 * @param props.isSingleFile    A boolean flag indicating whether only a single file can be uploaded.
 * @param props.name            The name identifier for the file input field used for form handling and validation.
 * @param props.maxFileSize     The max file size in bytes.
 * @param props.onChange        Callback function that gets executed when a user selects an option from the dropdown.
 * @param props.testId          The `testId` property represents a unique identifier for testing purposes.
 * @param props.validation      Optional validation function to validate the input value.
 *
 * @returns A React component that renders a file upload interface with drag-and-drop support and displays uploaded files with their progress and error states.
 */

const FileInput = observer(({
    CustomFileItem,
    defaultProgresses = {
        fileErrors: [],
        fileProgressBars: []
    },
    defaultValue = new DataTransfer().files,
    hints = [],
    infoText,
    isDisabled = false,
    isKeepOnUnmount = false,
    isSingleFile = false,
    label,
    // eslint-disable-next-line @nfq/no-magic-numbers
    maxFileSize = 500 * 1024 * 1024,
    name,
    onChange = async () => {},
    onDownload = async () => {},
    size = 'large',
    testId = 'FileInput',
    validation = () => null
// eslint-disable-next-line complexity
}: ComponentProps) => {
    const {
        colors,
        error,
        fieldRef,
        handleChange,
        handleDragOver,
        handleFileDelete,
        progresses,
        value
    } = useFileInput(
        name,
        defaultProgresses,
        defaultValue,
        onChange,
        maxFileSize,
        isKeepOnUnmount,
        validation
    );

    return (
        <Wrapper data-size={size}>
            <InputLabel>
                <LabelInfoButtonWrapper>
                    <InputLabel data-cy={`${testId}-label`}>{label}</InputLabel>
                    {infoText && (
                        <InfoPopup testId={`${testId}-info-popup`}>
                            <P $size="small" data-cy={`${testId}-info-text`}>{infoText}</P>
                        </InfoPopup>
                    )}
                </LabelInfoButtonWrapper>
                <Spacer y={3} />
                <AssetUploadWrapper $hasFiles={value.length > 0} data-cy={testId}>
                    <Upload
                        color1={colors.textSecondary}
                        height={getUploadIconSize(size)}
                        width={getUploadIconSize(size)}
                    />
                    <Spacer x={4} y={4} isNotStretching />
                    <P $size={size === 'small' ? 'small' : 'medium'}>
                        Drag & Drop oder <Color $color={colors.primary}>Datei auswählen</Color>
                    </P>
                    {size === 'large' && <Spacer y={2} isNotStretching />}
                    {size === 'large' && <P $size="small">max. {fileSizeStringFromBytes(maxFileSize)} Dateigröße</P>}
                    <Input
                        ref={fieldRef}
                        data-cy={`${testId}-input`}
                        data-has-error={error !== null && error.length > 0}
                        data-has-files-selected={value.length > 0}
                        disabled={isDisabled}
                        multiple={!isSingleFile}
                        type="file"
                        onChange={handleChange}
                        onDragOver={handleDragOver}
                    />
                </AssetUploadWrapper>
            </InputLabel>
            {Array.from(value, (file, index) => (
                <Fragment key={file.name}>
                    {CustomFileItem
                        ? (
                            <CustomFileItem
                                key={file.name}
                                index={index}
                                name={file.name}
                                progressData={progresses}
                                size={size}
                                testId={`${testId}-custom-file-item-${index}`}
                                onDelete={handleFileDelete}
                                onDownload={onDownload}
                            />
                        ) : (
                            <FileItem
                                key={file.name}
                                index={index}
                                name={file.name}
                                progressData={progresses}
                                size={size}
                                testId={`${testId}-custom-file-item-${index}`}
                                onDelete={handleFileDelete}
                                onDownload={onDownload}
                            />
                        )}
                    <Spacer y={3} />
                </Fragment>
            ))}
            {(error !== null && error.length > 0) && (
                <Hints baseTestId={testId} hints={error} hintType="error" paddingLeft={0} />
            )}
            {(error === null || error.length === 0) && <Hints baseTestId={testId} hints={hints} paddingLeft={0} />}
        </Wrapper>
    );
});

FileInput.displayName = 'FileInput';

export {FileInput};

const Wrapper = styled.div`
    ${getFileInputVariables}

    &:has(input[disabled]) {
        ${getFileInputDisabledVariables}
    }

    &:has(input[data-has-error=true]) {
        ${getFileInputErrorVariables}
    }

    &:focus-within {
        color: ${getFileInputFocusVariables};
    }

    &:has(input[data-has-files-selected=true]) {
        ${getFileInputFocusVariables}
    }
`;

const InputLabel = styled(Label)`
    color: var(--label-font-color);
    cursor: var(--cursor);
    width: 100%;
`;

const LabelInfoButtonWrapper = styled.div`
    align-items: center;
    display: flex;
    gap: ${spacing(2)};
`;

interface AssetUploadWrapperProps {
    $hasFiles: boolean;
}

const AssetUploadWrapper = styled.div<AssetUploadWrapperProps>`
    align-items: center;
    background-color: var(--file-input-background-color);
    border: 1px solid var(--file-input-border-color);
    border-radius: 8px;
    color: var(--file-input-font-color);
    display: ${({$hasFiles}) => ($hasFiles ? 'none' : 'flex')};
    flex-direction: var(--file-input-flex-direction);
    justify-content: center;
    padding: var(--file-input-padding);
    position: relative;

    &:hover {
        cursor: pointer;
    }
`;

const Input = styled.input`
    appearance: none;
    cursor: pointer;
    height: 100%;
    left: 0;
    opacity: 0;
    position: absolute;
    top: 0;
    width: 100%;
`;